<template>
  <div class="about-general-container">
    <header>
      <div class="header-logo">
        <router-link to="/map">
          <img src="../assets/logo.svg">
        </router-link>
      </div>
    </header>

    <!-- Mosaic banner -->
    <section
      class="mosaic"
      :style="gridStyle"
      v-if="gridStyle.gridTemplateRows"
    >
      <div
        v-for="idx in n"
        :key="idx"
      >
        <transition name="fade" mode="out-in">
          <div 
            v-if="Math.random() > .5"
            class="white-block"
            key="white"
          />
          <div
            v-else-if="Math.random() <= .5"
            class="black-block"
            key="black"
          />
          <div
            v-else
            class="img-block"
            key="img"
          >
            <img
              :src="`https://storage.googleapis.com/cartografiadigital/records_icons/${records[Math.floor(Math.random() * records.length)]}.png`"
            >
          </div>
        </transition>
      </div>
    </section>

    <section class="content">

      <!-- Options panel -->
      <div class="left-panel">
        <div class="options">
          <div
            v-for="(option, idx) in options"
            :key="idx"
            :class="{selected: idx === selected, first: idx === 0}"
            @click="selected = idx"
            class="option"
          >
            {{ option }}
          </div>
        </div>
        <div class="logos">
          <img src="../assets/imgs/fondecyt_logo.png">
          <img id="logo-facultad" class="margin-left" src="../assets/imgs/udp_logo.png">
          <img id="logo-laboratorio" class="margin-left" src="../assets/imgs/lab_logo.png">
        </div>
      </div>

      <!-- Selected option content -->
      <div class="right-panel">
        <div class="title">
          {{ options[selected] }}
        </div>

        <!-- Sobre el proyecto -->
        <div 
          class="option-body"
          v-if="selected === 0"
        >
          <p v-for="(line, idx) in aboutProject" :key="idx">
            {{ line }}
          </p>
          <img
            class="cc-logo"
            src="../assets/imgs/cc.jpg"
          >
          <span class="cc-legend">
            Esta obra está bajo una Licencia Creative Commons Atribución-NoComercial-CompartirIgual 4.0 Internacional
          </span>
        </div>

        <!-- Laboratorio Digital -->
        <div 
          class="option-body"
          v-if="selected === 1"
        >
          <p>
            El <a href="https://laboratoriodigital.udp.cl/">Laboratorio Digital</a> de la Facultad de Comunicación y Letras de la Universidad Diego Portales busca aportar con una reflexión crítica sobre las características y desafíos de la cultura digital en tres ejes: investigación interdisciplinaria, experimentación creativa con las tecnologías digitales, y propuestas para el desarrollo de políticas públicas en materia de cultura y sociedad digital. Un Laboratorio se define básicamente por ser un espacio de experimentación, lo cual constituye la esencia de nuestra propuesta: desarrollar y poner a prueba nuevas aproximaciones críticas a los fenómenos culturales a partir del diálogo con, y el uso de, tecnologías digitales, donde la investigación y el desarrollo de la creatividad son elementos cruciales para entender las complejidades de la cultura digital.
          </p>

        </div>

        <!-- Equipo -->
        <div 
          class="option-body"
          v-if="selected === 2"
        >
          <div v-for="member in team" :key="member.name">
            <p class="name"><span class="bold">{{ member.name }}</span> {{ member.role ? `(${member.role})` : '' }}</p>
            <p>{{ member.description }}</p>
          </div>

          <div v-if="colaborators.length">
            <p class="name"><span class="bold">Colaboradores</span></p>
            <p
              v-for="colaborator in colaborators"
              :key="colaborator"
              class="name"
            >{{ colaborator }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'About',
  metaInfo: {
    title: 'Sobre Cartografía Digital',
    titleTemplate: null,
    htmlAttrs: {
      lang: 'es'
    },
    meta: [
      { charset: 'utf-8' },
      { name: 'description',
        content: `
          Esta investigación busca presentar una cartografía crítica de la literatura digital latinoamericana en
          torno a cuatro dimensiones: una visualización de obras a publicarse en un sitio web, la cual contendrá
          las principales temáticas y géneros dentro de la literatura digital latinoamericana en castellano
          y portugués; problematizar las formas de archivo y preservación; el análisis del lenguaje digital y
          sus estéticas; y abordar la relación humano- máquina.
        `
      }
    ]
  },
  data(){
    return {
      n: 23*4,
      gridStyle: {
        display: 'grid',
        gridTemplateColumns: 'repeat(23, 1fr)',
        gridTemplateRows: null,
      },
      aboutProject: '',
      options: [
        'Sobre el Proyecto',
        'Laboratorio Digital',
        'Equipo'
      ],
      selected: 0,
    }
  },
  computed: {
    records: function(){return this.$store.getters.getRecords.map(record => record.metadata.code)},
  },
  created(){
    d3.text('https://storage.googleapis.com/cartografiadigital/sobre_el_proyecto.txt').then(d => {
      this.aboutProject = d.split('\n');
    })

    this.team = [];
    this.colaborators = [];
    d3.csv('https://storage.googleapis.com/cartografiadigital/equipo.csv').then(d => {
      d.forEach((row) => {
        if (row['Nombre'] !== 'Colaboradores') {
          this.team.push({
            name: row['Nombre'],
            role: row['Rol'],
            description: row['Descripción']
          })
        }
        else {
          this.colaborators.push(row['Descripción'])
        }
      });
    })
  },
  mounted(){
    const w = this.$el.clientWidth / 23;
    const h = w * 4 / 5;
    this.gridStyle.gridTemplateRows = `repeat(4, ${h}px)`;
  }
}
</script>

<style scoped>

  .about-general-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
  }

  header {
    display: flex;
    justify-content: space-between;
    height: 50px;
    margin: 0 40px;
    margin-top: 20px;
    margin-bottom: 40px;
    border-bottom: 1px solid var(--black);
    padding-bottom: 10px;
  }

  .header-logo {
    height: 100%;
  }

  .header-logo img {
    height: 100%;
  }

  .black-block {
    background-color: var(--black);
    width: 100%;
    height: 100%;
  }

  .content {
    width: 100%;
    padding: 0 40px;
    display: grid;
    grid-template-columns: 1fr 2fr;
    padding-top: 40px;
  }

  .left-panel {
    padding: 0 20px;
  }

  .options {
    font-family: var(--courier);
    font-size: 24px;
  }

  .option {
    cursor:pointer;
    border-bottom: 1px solid var(--black);
    padding: 10px 0 10px 0;
  }

  .option.first {
    border-top: 1px solid var(--black);
  }

  .selected, .bold {
    font-weight: bold;
  }

  .right-panel {
    padding-left: 40px;
    padding-right: 20px;
  }

  .title {
    font-family: var(--courier);
    font-size: 24px;
    font-weight: bold;
  }

  .option-body {
    font-family: var(--vesper);
    font-size: 20px;
  }

  .option-body p {
    margin-bottom: 20px;
  }

  .option-body .name {
    margin-bottom: 0;
  }

  .option-body a {
    text-decoration: underline;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity .4s;
  }
  
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }

  .cc-logo {
    width: 10%;
    margin-bottom: 5px;
  }

  .cc-legend {
    font-style: italic;
    font-size: 15px;
  }

  .logos {
    margin-top: 50px;
  }

  .logos img {
    width: 50%;
    margin-bottom: 30px;
  }

  #logo-laboratorio {
    width: 40%;
    margin-top: 56px;
  }

  .margin-left {
    margin-left: 18px;
  }

</style>